import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Header } from '../components/Header'
import { useAuth } from '../hooks/useAuth'
import { getItAssetsAccess } from '../lib/apiPhpAdapter'
import './ITAdminInfo.css'

const API_BASE_URL = window.location.origin + '/api'

type BlockType = 'text' | 'list' | 'link' | 'faq' | 'wifi' | 'contact'

interface SubBlock {
  id: string
  type: BlockType
  title?: string
  content: any
}

interface Block {
  id: string
  type: BlockType
  title: string
  content: any
  subBlocks?: SubBlock[]
  order: number
}

interface ITAdminContent {
  blocks?: Block[]
  // Старая структура для обратной совместимости
  announcement?: string
  support?: {
    email?: string
    phone?: string
    description?: string
  }
  wifi?: Array<{
    name: string
    ssid: string
    password: string
  }>
  office365?: {
    description?: string
    link?: string
  }
  sciobot?: {
    description?: string
    link?: string
  }
  security?: string[]
  services?: Array<{
    name: string
    description: string
  }>
  updates?: {
    description?: string
    lastUpdate?: string
  }
  faq?: Array<{
    question: string
    answer: string
  }>
}

const DEFAULT_CONTENT: ITAdminContent = {
  blocks: [
    {
      id: '1',
      type: 'text',
      title: '📢 Důležité oznámení',
      content: 'Vítejte v intranetovém systému školy. Tento systém slouží pro efektivní komunikaci a správu interních procesů školy.',
      order: 0
    }
  ]
}

export default function ITAdminInfo() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [content, setContent] = useState<ITAdminContent>(DEFAULT_CONTENT)
  const [loading, setLoading] = useState(true)
  const [showRestrictedTableButton, setShowRestrictedTableButton] = useState(false)

  useEffect(() => {
    loadContent()
  }, [])

  useEffect(() => {
    if (!user) {
      setShowRestrictedTableButton(false)
      return
    }
    let cancelled = false
    getItAssetsAccess()
      .then((r) => {
        if (!cancelled) setShowRestrictedTableButton(!!r.canAccess)
      })
      .catch(() => {
        if (!cancelled) setShowRestrictedTableButton(false)
      })
    return () => {
      cancelled = true
    }
  }, [user])

  const loadContent = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/it-admin-info`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.content) {
          setContent(data.content)
        }
      }
    } catch (error) {
      console.error('Error loading content:', error)
    } finally {
      setLoading(false)
    }
  }

  const renderBlock = (block: Block) => {
    switch (block.type) {
      case 'text':
        return (
          <section key={block.id} className="info-section">
            <h2>{block.title}</h2>
            <div className="announcement-box">
              <p>{block.content}</p>
            </div>
          </section>
        )

      case 'list':
        return (
          <section key={block.id} className="info-section">
            <h2>{block.title}</h2>
            <div className="security-box">
              <ul>
                {Array.isArray(block.content) && block.content.map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </section>
        )

      case 'link':
        return (
          <section key={block.id} className="info-section">
            <h2>{block.title}</h2>
            <div className="license-box">
              <p>{block.content?.description}</p>
              {block.content?.link && (
                <p style={{ marginTop: '1rem' }}>
                  <a
                    href={block.content.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#667eea',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '1.1rem',
                      wordBreak: 'break-all',
                      overflowWrap: 'break-word',
                      display: 'inline-block',
                      maxWidth: '100%'
                    }}
                  >
                    👉 {block.content.link}
                  </a>
                </p>
              )}
            </div>
          </section>
        )

      case 'faq':
        return (
          <section key={block.id} className="info-section">
            <h2>{block.title}</h2>
            <div className="faq-box">
              {Array.isArray(block.content) && block.content.map((item: any, index: number) => (
                <div key={index} className="faq-item">
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )

      case 'wifi':
        return (
          <section key={block.id} className="info-section">
            <h2>{block.title}</h2>
            <div className="wifi-box">
              {Array.isArray(block.content) && block.content.map((wifi: any, index: number) => (
                <div key={index} className="wifi-item">
                  <h3>🏫 {wifi.name}</h3>
                  <p><strong>Wi-Fi:</strong> {wifi.ssid}</p>
                  <p><strong>Heslo:</strong> {wifi.password}</p>
                </div>
              ))}
            </div>
          </section>
        )

      case 'contact':
        return (
          <section key={block.id} className="info-section">
            <h2>{block.title}</h2>
            <div className="support-box">
              <h3>Kontakt na IT oddělení</h3>
              <p>{block.content?.description}</p>
              <p>
                {block.content?.email && (
                  <>
                    <strong>Email:</strong> {block.content.email}<br />
                  </>
                )}
                {block.content?.phone && (
                  <>
                    <strong>Telefon:</strong> {block.content.phone}
                  </>
                )}
              </p>
            </div>
          </section>
        )

      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="it-admin-info">
        <Header
          leftButtons={
            <>
              <button
                onClick={() => navigate('/intranet')}
                className="back-button"
              >
                ← Zpět
              </button>
              <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Informace od IT administrátora</h1>
            </>
          }
          showLogo={true}
        />
        <div className="info-container">
          <div className="info-content">
            <div>Načítání...</div>
          </div>
        </div>
      </div>
    )
  }

  // Если есть новая структура с блоками, используем её
  const hasNewStructure = content.blocks && Array.isArray(content.blocks) && content.blocks.length > 0

  return (
    <div className="it-admin-info">
      <Header
        leftButtons={
          <>
            <button
              onClick={() => navigate('/intranet')}
              className="back-button"
            >
              ← Zpět
            </button>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Informace od IT administrátora</h1>
          </>
        }
        showLogo={true}
      />
      <div className="info-container">
        <div className="info-content">
          {showRestrictedTableButton && (
            <section className="info-section it-admin-restricted-cta">
              <button
                type="button"
                className="it-admin-restricted-cta__btn"
                onClick={() => navigate('/it-admin-info/prehled')}
              >
                Otevřít evidenci IT majetku
              </button>
            </section>
          )}
          {hasNewStructure ? (
            // Новая структура с блоками
            content.blocks!
              .sort((a, b) => a.order - b.order)
              .map(block => renderBlock(block))
          ) : (
            // Старая структура для обратной совместимости
            <>
              {content.announcement && (
                <section className="info-section">
                  <h2>📢 Důležité oznámení</h2>
                  <div className="announcement-box">
                    <p>{content.announcement}</p>
                  </div>
                </section>
              )}

              {content.support && (
                <section className="info-section">
                  <h2>💻 Technická podpora</h2>
                  <div className="support-box">
                    <h3>Kontakt na IT oddělení</h3>
                    <p>{content.support.description}</p>
                    <p>
                      <strong>Email:</strong> {content.support.email}<br />
                      <strong>Telefon:</strong> {content.support.phone}
                    </p>
                  </div>
                </section>
              )}

              {content.wifi && content.wifi.length > 0 && (
                <section className="info-section">
                  <h2>📶 Seznam školních Wi-Fi sítí</h2>
                  <div className="wifi-box">
                    {content.wifi.map((wifi, index) => (
                      <div key={index} className="wifi-item">
                        <h3>🏫 {wifi.name}</h3>
                        <p><strong>Wi-Fi:</strong> {wifi.ssid}</p>
                        <p><strong>Heslo:</strong> {wifi.password}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {content.office365 && (
                <section className="info-section">
                  <h2>📧 Školní licence Microsoft Office 365</h2>
                  <div className="license-box">
                    <p>{content.office365.description}</p>
                    {content.office365.link && (
                      <p style={{ marginTop: '1rem' }}>
                        <a
                          href={content.office365.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#667eea',
                            textDecoration: 'none',
                            fontWeight: '600',
                            fontSize: '1.1rem'
                          }}
                        >
                          👉 {content.office365.link}
                        </a>
                      </p>
                    )}
                  </div>
                </section>
              )}

              {content.sciobot && (
                <section className="info-section">
                  <h2>🤖 Školní licence Sciobot</h2>
                  <div className="license-box">
                    <p>{content.sciobot.description}</p>
                    {content.sciobot.link && (
                      <p style={{ marginTop: '1rem' }}>
                        <a
                          href={content.sciobot.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#667eea',
                            textDecoration: 'none',
                            fontWeight: '600',
                            fontSize: '1.1rem'
                          }}
                        >
                          🤖 {content.sciobot.link}
                        </a>
                      </p>
                    )}
                  </div>
                </section>
              )}

              {content.security && content.security.length > 0 && (
                <section className="info-section">
                  <h2>🔐 Bezpečnost</h2>
                  <div className="security-box">
                    <h3>Důležité bezpečnostní informace</h3>
                    <ul>
                      {content.security.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </section>
              )}

              {content.faq && content.faq.length > 0 && (
                <section className="info-section">
                  <h2>❓ Časté dotazy</h2>
                  <div className="faq-box">
                    {content.faq.map((item, index) => (
                      <div key={index} className="faq-item">
                        <h3>{item.question}</h3>
                        <p>{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
